import React, { useState } from 'react';
import { Search, Loader, FileText, Download } from 'lucide-react';
import { conciliationApi } from '../api/client';

const Conciliation = () => {
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await conciliationApi.getOrderConciliation(orderId.trim());
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch conciliation data');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!data) return;

    const headers = ['iPag Payment ID', 'Method', 'Amount', 'Installments', 'Interest Mode', 'Status', 'Paid Amount', 'Description'];
    const rows = data.transactions.map(t => [
      t.ipagPaymentId,
      t.method,
      t.amount,
      t.installments || '',
      t.interestMode || '',
      t.status,
      t.paidAmount,
      t.description,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conciliation-order-${data.orderNumber}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Conciliação de Pagamentos</h1>
        <p className="mt-2 text-sm text-gray-400">
          Visualize detalhes de transações e dados de conciliação de pedidos
        </p>
      </div>

      <div className="card card-body">
        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <label htmlFor="orderId" className="input-label">
              ID ou Número do Pedido
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="text"
                id="orderId"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md bg-dark-600 border border-dark-500 text-white focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Digite o ID ou número do pedido"
              />
              <button
                type="submit"
                disabled={loading || !orderId.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-dark-900 bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader className="h-5 w-5 animate-spin" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </form>

        {error && (
          <div className="mt-4 alert-error">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        {data && (
          <div className="mt-6 border-t border-dark-600 pt-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-lg font-medium text-white">
                  Pedido #{data.orderNumber}
                </h2>
                <p className="text-sm text-gray-300">
                  Total: {data.currency} {data.orderTotal.toFixed(2)} - Status: {data.status}
                </p>
                {data.paidAt && (
                  <p className="text-xs text-gray-400">
                    Paid at: {new Date(data.paidAt).toLocaleString()}
                  </p>
                )}
              </div>
              <button
                onClick={exportToCSV}
                className="btn-secondary inline-flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      ID PAGAMENTO IPAG
                    </th>
                    <th className="table-header-cell">
                      MÉTODO
                    </th>
                    <th className="table-header-cell">
                      VALOR
                    </th>
                    <th className="table-header-cell">
                      STATUS
                    </th>
                    <th className="table-header-cell">
                      DESCRIÇÃO
                    </th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {data.transactions.map((transaction, index) => (
                    <tr key={index} className="table-row">
                      <td className="table-cell font-medium">
                        {transaction.ipagPaymentId}
                      </td>
                      <td className="table-cell text-gray-300">
                        {transaction.method}
                        {transaction.installments && (
                          <span className="ml-1 text-xs">
                            ({transaction.installments}x)
                          </span>
                        )}
                      </td>
                      <td className="table-cell text-primary-500">
                        {data.currency} {transaction.amount.toFixed(2)}
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                          transaction.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : transaction.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.status}
                        </span>
                      </td>
                      <td className="table-cell text-gray-400">
                        {transaction.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 alert-info">
              <div className="flex">
                <FileText className="h-5 w-5 text-blue-300" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-200">
                    Informações de Conciliação
                  </h3>
                  <p className="mt-1 text-sm text-blue-300">
                    Use os IDs de Pagamento iPag e descrições acima para conciliar transações
                    no seu sistema financeiro.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Conciliation;
